import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import API_BASE_URL from "../../config";
import "./GovernorPanel.css";

const getToken = () => localStorage.getItem("userToken");

// Per-number/per-Apple-ID sending limits: daily new-contact quota, Leads vs Cold,
// and how many are queued. Reads the Core governor stats endpoint.
export default function GovernorPanel({ channel, ownerId }) {
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState(null); // null | 'lead' | 'cold' | 'stopped'
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  const baseUrl =
    channel === "whatsapp"
      ? `${API_BASE_URL}/whatsapp/governor/${ownerId}`
      : `${API_BASE_URL}/imessage/governor?handle_id=${ownerId}`;
  const sep = channel === "whatsapp" ? "?" : "&";

  const load = useCallback(
    async (state) => {
      setLoading(true);
      try {
        const t = getToken();
        const headers = t ? { Authorization: `Bearer ${t}` } : {};
        const url = state ? `${baseUrl}${sep}state=${state}` : baseUrl;
        const res = await axios.get(url, { headers, timeout: 15000 });
        setStats(res.data.stats);
        setContacts(res.data.contacts || []);
      } catch (e) {
        /* ignore */
      } finally {
        setLoading(false);
      }
    },
    [baseUrl, sep]
  );

  useEffect(() => {
    load(null);
  }, [load]);

  const pickTab = (t) => {
    const next = tab === t ? null : t;
    setTab(next);
    load(next || null);
  };

  if (!stats) return null;
  const pct = stats.dailyCap ? Math.min(100, Math.round((stats.newToday / stats.dailyCap) * 100)) : 0;
  const near = stats.dailyCap && stats.remaining <= Math.max(5, stats.dailyCap * 0.1);

  return (
    <div className="gov-panel">
      <div className="gov-quota">
        <div className="gov-quota-head">
          <span>New contacts today</span>
          <strong>
            {stats.newToday}
            {stats.dailyCap ? ` / ${stats.dailyCap}` : ""}
          </strong>
        </div>
        <div className="gov-bar">
          <div className={`gov-bar-fill ${near ? "warn" : ""}`} style={{ width: pct + "%" }} />
        </div>
        <div className="gov-quota-sub">
          {stats.remaining != null ? `${stats.remaining} new left today` : "no cap"} · {stats.queued} queued
        </div>
      </div>

      <div className="gov-chips">
        <button className={`gov-chip lead ${tab === "lead" ? "on" : ""}`} onClick={() => pickTab("lead")}>
          Leads <b>{stats.leads}</b>
        </button>
        <button className={`gov-chip cold ${tab === "cold" ? "on" : ""}`} onClick={() => pickTab("cold")}>
          Cold <b>{stats.cold}</b>
        </button>
        <button className={`gov-chip stopped ${tab === "stopped" ? "on" : ""}`} onClick={() => pickTab("stopped")}>
          Stopped <b>{stats.stopped}</b>
        </button>
      </div>

      {tab && (
        <div className="gov-list">
          {loading ? (
            <div className="gov-muted">Loading…</div>
          ) : contacts.length === 0 ? (
            <div className="gov-muted">No {tab} contacts.</div>
          ) : (
            contacts.map((c, i) => (
              <div key={i} className="gov-row">
                <span className="gov-contact">{c.contact}</span>
                <span className="gov-meta">
                  {c.outbound_count} sent{c.responded_at ? " · replied" : ""}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
